import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Utils, {dateComparator, formatMoney} from "../../../../../../core/services/utils/utils";
import {routeFunctions} from "../../../../../../core/models/static/routes/app-routes";
import {SinglePromotionDataGridCellKeys} from "../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../containers/data-grid";
import IconButton from "../../../../../components/base/button/icon_button";
import {ArrowBack, ChevronRightRounded} from "@mui/icons-material";
import {Button, Paper, useTheme} from "@mui/material";
import {Col} from "reactstrap";
import ToggleButtons from "../../../../../components/base/button/toggle_buttons";
import LineChart from "../../../../../components/base/chart/line-chart";
import PromotionsSingleSearchSection from "../../../../../components/search-sections/promotions-single-search-section";
import Api from "../../../../../../core/services/api/api";
import classnames from "classnames";
import useSearch from "../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../core/services/date-utils";
import {Link, useHistory, useParams} from "react-router-dom";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../hooks/use-access-levels";

const dataGridSaveConfig = {
    name: 'PromotionsInformationViewDataGrid',
    version: 1,
}

const PromotionsSingleView = () => {
    const params = useParams();
    const history = useHistory();
    const [selectedPromotion, setSelectedPromotion] = useState(null);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('promotions-information-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const canExportPromotionsList = operatorAccessLevels.packages.sales.exportPromotionsList;

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.promotionInformation(params.promotionId));
        configureBehavioralTool((tags) => tags.promotionInformation(params.promotionId));
    }, [configureBehavioralTool, configureNotebookTags, params.promotionId]);

    /**
     * Gets promotions from server by calling the api regarding this action
     * Populates the [data] in the state with result if api call is successful
     * @type {function(): Promise<void>}
     */
    const getPromotion = useCallback(async () => {
        setLoading(true);
        const response = await Api.getActiveCoupons();
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            const promotion = (response?.data ?? []).find(e => e.id.toString() === params.promotionId);
            setSelectedPromotion(promotion);
        }
    }, [isMounted, params.promotionId])

    /**
     * with each change in the [params]'s [promotionId]
     * - fetches the promotion information from the server
     */
    useEffect(() => {
        void getPromotion();
    }, [getPromotion])

    /**
     * Gets the information of the promo code from server:
     * Calls the api regarding this action
     * if the api call was successful, populates [data] in the inner state
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const search = useCallback(async (args) => {
        const forApi = {
            BudgetId: params.promotionId,
            StartDateTime: args.filters?.startDate,
            EndDateTime: args.filters?.endDate,
        };
        const response = await Api.getBudgetHistory(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(response?.data ?? [])
        }
    }, [isMounted, params.promotionId])

    const searchData = useSearch(search)

    /**
     * Returns the object for exporting the list of drivers by preparing the title and body of the csv file
     * @return {{title: string, body: (*|*[])}}
     */
    const formatExportingData = async () => {
        return {
            title: `"${selectedPromotion.code}" redeems`,
            body: data?.map((e, index) => ({
                '   ': `${index + 1}`,
                'Customer': e.customerName ?? '--',
                'Card Number': e.cardNumber ?? '--',
                'Amount': formatMoney(e.amount) ?? '--',
                'Redeem Date & Time': e.redeemedDateTime ? DateUtils.serverToLocal(e.redeemedDateTime).format(DateUtils.dateTimeFormat) : '--',
            })) ?? [],
        }
    }

    /**
     * Memo version of column in the data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Customer",
                name: SinglePromotionDataGridCellKeys.customer,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 170,
            },
            {
                title: "Amount",
                name: SinglePromotionDataGridCellKeys.amount,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 110,
            },
            {
                title: "Redeem Date & Time",
                name: SinglePromotionDataGridCellKeys.redeemDateTime,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.dateTime,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 160,
            },
            {
                title: "Card Number",
                name: SinglePromotionDataGridCellKeys.cardNumber,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 140,
            },
        ], [])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });


    /**
     * Data grid rows entities
     * @type {DataGridRow[]}
     */
    const rows = useMemo(() => {
        return data?.map(item => ({
            key: item.id,
            data: item,
            cells: {
                [SinglePromotionDataGridCellKeys.customer]:
                    <Button
                        className={classnames('part-number-text-button')}
                        variant={'text'}
                        component={Link}
                        to={routeFunctions.customer.generalInformation.profile(item.customerId)}
                    >
                        <p>{item.customerName}</p>
                        <ChevronRightRounded className='icon small' color={"primary"}/>
                    </Button>,
                [SinglePromotionDataGridCellKeys.amount]: item.amount,
                [SinglePromotionDataGridCellKeys.redeemDateTime]: item.redeemedDateTime,
                [SinglePromotionDataGridCellKeys.cardNumber]: Utils.formatCardNumber(item.cardNumber),
            },
        }))
    }, [data])

    return (
        <>
            <div className={'d-flex align-items-center ml-2 my-4'}>
                <IconButton
                    onPressed={() => history.goBack()}
                    color={'paper.secondary'}
                >
                    <ArrowBack className='icon normal'/>
                </IconButton>
                <p className={'view-title ml-2'}>
                    {selectedPromotion?.code ?? ''}
                </p>
            </div>
            <PromotionsSingleSearchSection searchData={searchData}/>
            <div className={'mb-3'}/>
            <Paper className={'paper d-flex flex-column'}>
                <ChartSection dataset={data} loading={searchData.loading || loading}/>
            </Paper>
            <div className={'mb-3'}/>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                columns={dataGridColumns}
                hideFooter
                rows={rows}
                state={{
                    loading: {state: searchData.loading || loading},
                    density: dataGridDensity,
                }}
                visibleRows={10}
                getExportInfo={canExportPromotionsList ? formatExportingData : undefined}
            />
        </>
    );
}

/**
 * Types of available visibilities for the chart
 */
const chartVisibilityTypes = {
    yearly: 'yearly',
    monthly: 'monthly',
    daily: 'daily',
    hourly: 'hourly',
    general: 'general'
}

/**
 * Chart Component shown int the login logs
 * @param dataset
 * @param loading
 * @param valueAggregator
 * @return {JSX.Element}
 * @constructor
 */
const ChartSection = ({dataset, loading}) => {
    const theme = useTheme();
    const chartColors = useMemo(() => Object.values(theme.palette['app-specific'].chart), [theme.palette])
    const [visibility, setVisibility] = useState(chartVisibilityTypes.general);
    const visibilities = useMemo(() => [
        {
            value: chartVisibilityTypes.yearly,
            name: "Yearly",
            disabled: loading
        },
        {
            value: chartVisibilityTypes.monthly,
            name: "Monthly",
            disabled: loading
        },
        {
            value: chartVisibilityTypes.daily,
            name: "Daily",
            disabled: loading
        },
        {
            value: chartVisibilityTypes.hourly,
            name: "Hourly",
            disabled: loading
        },
        {
            value: chartVisibilityTypes.general,
            name: "General",
            disabled: loading
        }
    ], [loading])


    /**
     * Gets the string representation of the day of the week of a date
     * @param {DateUtilsTypes.Instance} date
     * @return {string}
     */
    const dayOfWeek = (date) => {
        const dow = date.day();
        switch (dow) {
            case 0:
            default:
                return "Sunday";
            case 1:
                return "Monday";
            case 2:
                return "Tuesday";
            case 3:
                return "Wednesday";
            case 4:
                return "Thursday";
            case 5:
                return "Friday";
            case 6:
                return "Saturday";

        }
    }

    /**
     * Formats the hour returned from [hour] and returns the string representation
     * @param {number} hour
     */
    const formatHour = (hour) => hour < 10 ? `0${hour}:00` : `${hour}:00`

    /**
     * Gets the data set ready for the chart by aggregating them based on the selected visibility type
     * @param data
     * @return {object}
     */
    const getDatasetData = useCallback((data = []) => {
        const aggregated = {};
        switch (visibility) {
            case  chartVisibilityTypes.hourly: {
                data
                    ?.sort((a, b) => DateUtils.serverToLocal(a.redeemedDateTime).hour() - DateUtils.serverToLocal(b.redeemedDateTime).hour())
                    ?.forEach(record => {
                        const dow = formatHour(DateUtils.serverToLocal(record.redeemedDateTime).hour());
                        aggregated[dow] = !!aggregated[dow] ? aggregated[dow] += 1 : 1;
                    })
                break;
            }
            case  chartVisibilityTypes.daily: {
                data
                    ?.sort((a, b) => DateUtils.serverToLocal(a.redeemedDateTime).day() - DateUtils.serverToLocal(b.redeemedDateTime).day())
                    ?.forEach(record => {
                        const dow = dayOfWeek(DateUtils.serverToLocal(record.redeemedDateTime));
                        aggregated[dow] = !!aggregated[dow] ? aggregated[dow] += 1 : 1;
                    })
                break;
            }
            case  chartVisibilityTypes.monthly: {
                data
                    ?.sort((a, b) => dateComparator(a.redeemedDateTime, b.redeemedDateTime))
                    ?.forEach(record => {
                        const date = DateUtils.serverToLocal(record.redeemedDateTime).format('MMM');
                        aggregated[date] = !!aggregated[date] ? aggregated[date] += 1 : 1;
                    })
                break;
            }
            case  chartVisibilityTypes.yearly: {
                data
                    ?.sort((a, b) => dateComparator(a.redeemedDateTime, b.redeemedDateTime))
                    ?.forEach(record => {
                        const date = DateUtils.serverToLocal(record.redeemedDateTime).format('YYYY');
                        aggregated[date] = !!aggregated[date] ? aggregated[date] += 1 : 1;
                    })
                break;
            }
            case  chartVisibilityTypes.general: {
                data
                    ?.sort((a, b) => dateComparator(a.redeemedDateTime, b.redeemedDateTime))
                    ?.forEach(record => {
                        const date = DateUtils.serverToLocal(record.redeemedDateTime).format(DateUtils.dateFormat);
                        aggregated[date] = !!aggregated[date] ? aggregated[date] += 1 : 1;
                    })
                break;
            }
            default:
                break;
        }
        return aggregated;
    }, [visibility])

    /**
     * Memo version of chart data passed to the
     * @type {{datasets: *[], labels: *[]}}
     */
    const chartData = useMemo(() => {
        const chartData = {
            datasets: [],
            labels: [],
        }
        const _data = getDatasetData(dataset);
        chartData.datasets.push({
            data: _data,
            label: 'Number of Redeems',
            backgroundColor: chartColors[0],
            borderColor: chartColors[0],
            pointBackgroundColor: chartColors[0],
            pointBorderColor: chartColors[0],
            borderWidth: 1.5,
            cubicInterpolationMode: 'monotone',
        });
        chartData.labels = Object.keys(_data) ?? [];
        return chartData;
    }, [chartColors, dataset, getDatasetData]);

    return (
        <>
            <Col xs={12}
                 className={'my-4 transition mx-auto bar-chart'}>
                <LineChart height={window.innerHeight / 3} data={chartData}/>
            </Col>
            <Col xs={12} className={'d-flex justify-content-center mb-4'}>
                <ToggleButtons
                    size={'medium'}
                    value={visibility}
                    onChange={(_, v) => setVisibility(v)}
                    options={visibilities}
                />
            </Col>
        </>
    );

}

export default PromotionsSingleView;
